import { camelCase, snakeCase, snake } from "change-case";
import axios from "axios";
import qs from "query-string";

import config from "config";
import { getCookie } from "lib/cookie";
import { SortDirection } from "lib/enums";

interface LoginRequest {
  username: string;
  password: string;
  remember?: boolean;
}

interface LoginResponse {
  token: string;
}

export default class Service {
  constructor() {
    if (!axios.defaults.baseURL) {
      axios.defaults.baseURL = config.baseUrl;
    }
  }

  public async get(action: string, options?: any): Promise<any> {
    return axios
      .get(`${action}/`, {
        params: this.toSnake(options),
        paramsSerializer: qs.stringify,
      })
      .then((res) => this.toCamel(res.data))
      .catch(this.catchException);
  }

  public async post(
    action: string,
    formData: any,
    options?: any,
  ): Promise<any> {
    return axios
      .post(`${action}/`, this.toSnake(formData), options)
      .then((res) => this.toCamel(res.data));
  }

  public async postWithFormData(
    action: string,
    formData: any,
    options?: any,
  ): Promise<any> {
    return axios
      .post(`${action}/`, this.toSnakeFormData(formData), options)
      .then((res) => this.toCamel(res.data));
  }

  public async put(
    action: string,
    formData?: any,
    options?: any,
  ): Promise<any> {
    return axios
      .put(`${action}/`, this.toSnake(formData), options)
      .then((res) => this.toCamel(res.data));
  }

  public async putWithFormData(
    action: string,
    formData?: any,
    options?: any,
  ): Promise<any> {
    return axios
      .put(`${action}/`, this.toSnakeFormData(formData), options)
      .then((res) => this.toCamel(res.data));
  }

  public async delete(action: string, options?: any): Promise<any> {
    return axios
      .delete(`${action}/`, options)
      .then((res) => this.toCamel(res.data));
  }

  public login(options?: LoginRequest): Promise<LoginResponse> {
    return this.post("/api-token-auth", options);
  }

  public refreshToken(token?: string) {
    if (!token) {
      const username = getCookie("username");
      const password = getCookie("password");
      return this.login({ username, password }).then((res) => res.token);
    }
    delete axios.defaults.headers.common.Authorization;
    return this.post("/api-token-refresh", { token }).then((res) => res.token);
  }

  public ordering(orderBy: string, sort: string) {
    if (!orderBy) {
      return undefined;
    }
    return sort === SortDirection.DESC ? `-${orderBy}` : orderBy;
  }

  private catchException = (e: any) => {
    if (e.status === 401 || (e.response && e.response.status === 401)) {
      this.refreshToken();
    }
    throw e;
  };

  private toSnake(data: any): any {
    if (!data) {
      return undefined;
    }
    if (data instanceof Array) {
      return data.map((v) => {
        if (!!v && typeof v === "object") {
          return this.toSnake(v);
        }
        return v;
      });
    } else {
      const returnData = {};
      Object.keys(data).forEach((key) => {
        const newKey = snakeCase(key);
        const value = data[key];

        if (
          value instanceof Array ||
          (value !== null &&
            value !== undefined &&
            value.constructor === Object)
        ) {
          returnData[newKey] = this.toSnake(value);
        } else {
          returnData[newKey] = value;
        }
      });
      return returnData;
    }
  }

  private toSnakeFormData(data: any, previousKey?: string): any {
    if (!data) {
      return undefined;
    }
    if (data instanceof Array) {
      return data.map((v) => {
        if (!!v && typeof v === "object") {
          return this.toSnakeFormData(v);
        }
        return v;
      });
    } else {
      const returnData = new FormData();
      Object.keys(data).forEach((key) => {
        let newKey = snakeCase(key);
        const value = data[key];
        if (
          value instanceof Array ||
          (value !== null && value.constructor === Object)
        ) {
          if (previousKey) {
            newKey = `${previousKey}[${newKey}]`;
          }
          const newValue = this.toSnakeFormData(value, newKey);
          newValue.forEach((val: any) => {
            returnData.append(`${newKey}[]`, val);
          });
        } else {
          returnData.append(newKey, value);
        }
      });
      return returnData;
    }
  }

  private toCamel(data: any): any {
    if (data instanceof Array) {
      return data.map((v) => {
        if (!!v && typeof v === "object") {
          return this.toCamel(v);
        }
        return v;
      });
    } else {
      const returnData = {};
      Object.keys(data).forEach((key) => {
        const newKey = camelCase(key);
        const value = data[key];
        if (
          value instanceof Array ||
          (value !== null &&
            value !== undefined &&
            value.constructor === Object)
        ) {
          returnData[newKey] = this.toCamel(value);
        } else {
          returnData[newKey] = value;
        }
      });
      return returnData;
    }
  }
}

export type { LoginRequest, LoginResponse };
